import React from "react";
import { useShallow } from "zustand/react/shallow";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "../ui/Collapsible";
import Button from "../ui/Button";
import Separator from "../ui/Separator";
import Tooltip from "../ui/Tooltip";
import { Pause, Play, Settings, ShieldCheck, SlidersHorizontal, Square } from "lucide-react";
import { usePhysicsStore } from "../../stores/physicsStore";
import {
  useModelValidationActions,
  useModelValidationIsStale,
  useModelValidationStatus,
  useModelValidationSummary,
} from "../../stores/modelValidationStore";
import { isPhysicsCapabilityUnavailableError } from "../../stores/physics/physicsUserErrors";
import { getPhysicsAuthoringLockMessage, usePhysicsAuthoringLock } from "../../utils/physicsGuards";

function mainMenuPhysicsErrorLabel(lastError) {
  if (!lastError) return "Failed";
  if (isPhysicsCapabilityUnavailableError(lastError)) return "Unavailable";
  const lower = String(lastError).toLowerCase();
  if (
    lower.includes("timeout") ||
    lower.includes("did not respond") ||
    lower.includes("no response")
  ) {
    return "Timed out";
  }
  return "Failed";
}

const formatIssueCount = (count, singular, plural = `${singular}s`) =>
  `${count} ${count === 1 ? singular : plural}`;

function getModelCheckIndicator({ status, isStale, summary, error }) {
  const staleSuffix = isStale ? " from the last check; scene has changed" : "";
  if (status === "error") {
    return {
      color: "#f87171",
      label: error ? `check failed: ${error}` : "check failed",
    };
  }
  if (summary.errorCount > 0) {
    return {
      color: "#f87171",
      label: `${formatIssueCount(summary.errorCount, "error")}${staleSuffix}`,
    };
  }
  if (summary.warningCount > 0) {
    return {
      color: "#fcd34d",
      label: `${formatIssueCount(summary.warningCount, "warning")}${staleSuffix}`,
    };
  }
  return null;
}

function useModelCheckStatusLight() {
  const status = useModelValidationStatus();
  const isStale = useModelValidationIsStale();
  const { summary, error } = useModelValidationSummary();
  const { run } = useModelValidationActions();

  React.useEffect(() => {
    if (status === "checking") return undefined;
    if (status !== "idle" && !isStale) return undefined;
    const timeout = window.setTimeout(
      () => {
        run();
      },
      status === "idle" ? 0 : 250
    );
    return () => window.clearTimeout(timeout);
  }, [isStale, run, status]);

  return React.useMemo(
    () => getModelCheckIndicator({ status, isStale, summary, error }),
    [error, isStale, status, summary]
  );
}

const PhysicsControlsButtonGrid = React.memo(function PhysicsControlsButtonGrid({
  isPhysicsRunning,
  isPhysicsPaused,
  isModelCheckOpen,
  onStartStop,
  onPauseResume,
  onOpenModelCheckPanel,
  onOpenRuntimeControlsPanel,
  onOpenPhysicsPanel,
  physicsSettingsLocked,
  physicsSettingsLockTooltip,
}) {
  const modelCheckIndicator = useModelCheckStatusLight();
  const checkModelTooltip = modelCheckIndicator
    ? `Check Model: ${modelCheckIndicator.label}`
    : "Check Model";
  const checkModelAriaLabel = modelCheckIndicator
    ? `Check Model (${modelCheckIndicator.label})`
    : "Check Model";

  return (
    <div className="rounded-md border border-gray-600 p-1">
      {/* Each ghost Button below sets backgroundColor inline to defeat the Button
          component's own surface styling; a `bg-transparent` class loses on source order. */}
      <div className="grid grid-cols-5 gap-1">
        <Button
          variant="ghost"
          size="small"
          className="h-7 w-full rounded-md border border-gray-700 flex items-center justify-center"
          style={{ backgroundColor: "transparent" }}
          onClick={onStartStop}
          aria-label={isPhysicsRunning ? "Stop physics" : "Start physics"}
        >
          {isPhysicsRunning ? <Square className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </Button>
        <Button
          variant="ghost"
          size="small"
          className="h-7 w-full rounded-md border border-gray-700 flex items-center justify-center"
          style={{ backgroundColor: "transparent" }}
          onClick={onPauseResume}
          disabled={!isPhysicsRunning && !isPhysicsPaused}
          aria-label={isPhysicsPaused ? "Resume physics" : "Pause physics"}
        >
          {isPhysicsPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
        </Button>
        <Tooltip content={checkModelTooltip} side="top" fullWidth>
          <Button
            variant="ghost"
            size="small"
            className="relative h-7 w-full rounded-md border border-gray-700 p-0"
            style={{ backgroundColor: isModelCheckOpen ? "rgba(55, 65, 81, 0.65)" : "transparent" }}
            onClick={onOpenModelCheckPanel}
            aria-label={checkModelAriaLabel}
            aria-pressed={isModelCheckOpen}
          >
            <span
              className="pointer-events-none flex items-center justify-center"
              style={{ position: "absolute", inset: 0 }}
            >
              <ShieldCheck className="h-4 w-4 shrink-0" />
            </span>
            {modelCheckIndicator ? (
              <span
                className="pointer-events-none"
                style={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  width: 8,
                  height: 8,
                  borderRadius: 9999,
                  backgroundColor: modelCheckIndicator.color,
                }}
                aria-hidden
              />
            ) : null}
          </Button>
        </Tooltip>
        <Tooltip content="Runtime Controls & Stats" side="top" fullWidth>
          <Button
            variant="ghost"
            size="small"
            className="h-7 w-full rounded-md border border-gray-700 flex items-center justify-center"
            style={{ backgroundColor: "transparent" }}
            onClick={onOpenRuntimeControlsPanel}
            aria-label="Runtime Controls & Stats"
          >
            <SlidersHorizontal className="w-4 h-4" />
          </Button>
        </Tooltip>
        <Tooltip
          content={physicsSettingsLocked ? physicsSettingsLockTooltip : "Physics Settings"}
          side="top"
          fullWidth
        >
          <Button
            variant="ghost"
            size="small"
            className="h-7 w-full rounded-md border border-gray-700 flex items-center justify-center"
            style={{ backgroundColor: "transparent" }}
            onClick={onOpenPhysicsPanel}
            disabled={physicsSettingsLocked}
            aria-label="Physics Settings"
          >
            <Settings className="w-4 h-4" />
          </Button>
        </Tooltip>
      </div>
    </div>
  );
});

const PhysicsControlsSection = React.memo(function PhysicsControlsSection({
  physicsStatus,
  physicsStatusColor,
  physicsLastError,
  isPhysicsRunning,
  isPhysicsPaused,
  isModelCheckOpen,
  onStartStop,
  onPauseResume,
  onOpenModelCheckPanel,
  onOpenRuntimeControlsPanel,
  onOpenPhysicsPanel,
  physicsSettingsLocked,
  physicsSettingsLockTooltip,
}) {
  const physicsStatusLabel =
    physicsStatus === "error" ? mainMenuPhysicsErrorLabel(physicsLastError) : physicsStatus;
  const isCapabilityUnavailable =
    physicsStatus === "error" && isPhysicsCapabilityUnavailableError(physicsLastError);

  return (
    <>
      <Collapsible defaultOpen>
        <CollapsibleTrigger className="select-none text-gray-200 flex items-center gap-2">
          <span>Physics</span>
          <span
            className={`flex items-center gap-1.5 text-xs font-normal${
              physicsStatus === "error"
                ? isCapabilityUnavailable
                  ? " text-amber-300"
                  : " text-red-300"
                : ""
            }`}
          >
            <span
              aria-hidden
              className="inline-block h-2 w-2 shrink-0 rounded-full"
              style={{ backgroundColor: physicsStatusColor }}
            />
            <span>{physicsStatusLabel}</span>
          </span>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="pt-1">
            <PhysicsControlsButtonGrid
              isPhysicsRunning={isPhysicsRunning}
              isPhysicsPaused={isPhysicsPaused}
              isModelCheckOpen={isModelCheckOpen}
              onStartStop={onStartStop}
              onPauseResume={onPauseResume}
              onOpenModelCheckPanel={onOpenModelCheckPanel}
              onOpenRuntimeControlsPanel={onOpenRuntimeControlsPanel}
              onOpenPhysicsPanel={onOpenPhysicsPanel}
              physicsSettingsLocked={physicsSettingsLocked}
              physicsSettingsLockTooltip={physicsSettingsLockTooltip}
            />
          </div>
        </CollapsibleContent>
      </Collapsible>
      <Separator />
    </>
  );
});

/** Narrow viewport only: same controls as main-menu Physics strip, fixed bottom-center. */
const MobilePhysicsControlsPill = React.memo(function MobilePhysicsControlsPill({
  physicsStatus,
  physicsStatusColor,
  physicsLastError,
  isPhysicsRunning,
  isPhysicsPaused,
  isModelCheckOpen,
  onStartStop,
  onPauseResume,
  onOpenModelCheckPanel,
  onOpenRuntimeControlsPanel,
  onOpenPhysicsPanel,
  physicsSettingsLocked,
  physicsSettingsLockTooltip,
}) {
  const physicsStatusLabel =
    physicsStatus === "error" ? mainMenuPhysicsErrorLabel(physicsLastError) : physicsStatus;
  const isCapabilityUnavailable =
    physicsStatus === "error" && isPhysicsCapabilityUnavailableError(physicsLastError);

  return (
    // Inline style keeps `env(safe-area-inset-bottom)` + `max()` readable on one line
    // and pins the WebKit backdrop-filter prefix next to its standard counterpart.
    <div
      className="pointer-events-auto fixed left-1/2 z-[19] flex max-w-[calc(100vw-24px)] -translate-x-1/2 flex-col items-center gap-1.5 rounded-md border border-gray-700 px-3 py-2 shadow-xl select-none"
      style={{
        bottom: "max(12px, env(safe-area-inset-bottom, 0px))",
        backgroundColor: "var(--panel-surface)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
      }}
      role="toolbar"
      aria-label="Physics Controls"
    >
      <div className="flex max-w-full items-center justify-center gap-1.5 text-xs font-normal">
        <span className="shrink-0 font-medium text-gray-300">Physics Controls</span>
        <span
          aria-hidden
          className="inline-block h-2 w-2 shrink-0 rounded-full"
          style={{ backgroundColor: physicsStatusColor }}
        />
        <span
          className={`min-w-0 truncate text-gray-200${
            physicsStatus === "error"
              ? isCapabilityUnavailable
                ? " text-amber-300"
                : " text-red-300"
              : ""
          }`}
        >
          {physicsStatusLabel}
        </span>
      </div>
      <PhysicsControlsButtonGrid
        isPhysicsRunning={isPhysicsRunning}
        isPhysicsPaused={isPhysicsPaused}
        isModelCheckOpen={isModelCheckOpen}
        onStartStop={onStartStop}
        onPauseResume={onPauseResume}
        onOpenModelCheckPanel={onOpenModelCheckPanel}
        onOpenRuntimeControlsPanel={onOpenRuntimeControlsPanel}
        onOpenPhysicsPanel={onOpenPhysicsPanel}
        physicsSettingsLocked={physicsSettingsLocked}
        physicsSettingsLockTooltip={physicsSettingsLockTooltip}
      />
    </div>
  );
});

/**
 * Physics chrome subscribes here (not on `ModernMainPanel`) so the main shell does not repaint
 * on physics worker/store churn during loads.
 */
function useMainMenuPhysicsState() {
  const { isRunning, isPaused, status, lastError } = usePhysicsStore(
    useShallow((s) => ({
      isRunning: s.isRunning,
      isPaused: s.isPaused,
      status: s.status,
      lastError: s.lastError,
    }))
  );
  const physicsStatusColor = React.useMemo(() => {
    switch (status) {
      case "running":
        return "#4ade80";
      case "starting":
      case "paused":
        return "#fbbf24";
      case "error":
        if (isPhysicsCapabilityUnavailableError(lastError)) return "#fbbf24";
        return "#f87171";
      default:
        return "#9ca3af";
    }
  }, [lastError, status]);
  return { isRunning, isPaused, status, lastError, physicsStatusColor };
}

const MainPanelDesktopPhysicsSection = React.memo(function MainPanelDesktopPhysicsSection({
  onStartStop,
  onPauseResume,
  isModelCheckOpen,
  onOpenModelCheckPanel,
  onOpenRuntimeControlsPanel,
  onOpenPhysicsPanel,
}) {
  const phy = useMainMenuPhysicsState();
  const physicsSettingsLocked = usePhysicsAuthoringLock();
  const physicsSettingsLockTooltip = React.useMemo(
    () => getPhysicsAuthoringLockMessage("edit"),
    []
  );
  return (
    <PhysicsControlsSection
      physicsStatus={phy.status}
      physicsStatusColor={phy.physicsStatusColor}
      physicsLastError={phy.lastError}
      isPhysicsRunning={phy.isRunning}
      isPhysicsPaused={phy.isPaused}
      isModelCheckOpen={isModelCheckOpen}
      onStartStop={onStartStop}
      onPauseResume={onPauseResume}
      onOpenModelCheckPanel={onOpenModelCheckPanel}
      onOpenRuntimeControlsPanel={onOpenRuntimeControlsPanel}
      onOpenPhysicsPanel={onOpenPhysicsPanel}
      physicsSettingsLocked={physicsSettingsLocked}
      physicsSettingsLockTooltip={physicsSettingsLockTooltip}
    />
  );
});

const MainPanelMobilePhysicsOverlay = React.memo(function MainPanelMobilePhysicsOverlay({
  actuatorControlsOpen,
  onStartStop,
  onPauseResume,
  isModelCheckOpen,
  onOpenModelCheckPanel,
  onOpenRuntimeControlsPanel,
  onOpenPhysicsPanel,
}) {
  const phy = useMainMenuPhysicsState();
  const physicsSettingsLocked = usePhysicsAuthoringLock();
  const physicsSettingsLockTooltip = React.useMemo(
    () => getPhysicsAuthoringLockMessage("edit"),
    []
  );
  if (actuatorControlsOpen) return null;
  return (
    <MobilePhysicsControlsPill
      physicsStatus={phy.status}
      physicsStatusColor={phy.physicsStatusColor}
      physicsLastError={phy.lastError}
      isPhysicsRunning={phy.isRunning}
      isPhysicsPaused={phy.isPaused}
      isModelCheckOpen={isModelCheckOpen}
      onStartStop={onStartStop}
      onPauseResume={onPauseResume}
      onOpenModelCheckPanel={onOpenModelCheckPanel}
      onOpenRuntimeControlsPanel={onOpenRuntimeControlsPanel}
      onOpenPhysicsPanel={onOpenPhysicsPanel}
      physicsSettingsLocked={physicsSettingsLocked}
      physicsSettingsLockTooltip={physicsSettingsLockTooltip}
    />
  );
});

export { MainPanelDesktopPhysicsSection, MainPanelMobilePhysicsOverlay };
